import type { Account, Pixel } from '@adtrackify/at-tracking-event-types';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@/components/ui/helpers/toast';

import { useAuthenticatedQuery } from '@/hooks/auth/use-auth-query-hooks';

import { apiErrorLogger, apiInfoLogger } from '@/lib/monitoring/api-logger';
import * as Logger from '@/lib/monitoring/logger';
import { accountKeys } from '@/features/workspace/api/account-keys';
import { paramsToObject } from '@/lib/helpers/url/params';
import { useAccountActions, useAccountState, useIsAccountTransitioning } from '@/contexts/account/account-context';
import { useAuthToken } from '@/contexts/auth/auth-context';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import {
  AccountsApiService,
} from '@/services/internal-api/accounts/accounts-api-service';
import type { AccountListResponse } from '@/services/internal-api/accounts/types/accounts-api-service-types';
import { useGlobalDialogStore } from '@/store/global-dialog/use-global-dialog-store';
import useStore from '@/store/app/use-store';
import { prefetchAccountData } from '../api/prefetch-account-data';

const findAccountByAid = (accounts: Account[], aid: string): Account | undefined => {
  // aid is the account ID (same as pixel.accountId for any pixel in the account)
  // Compare directly against account.id for simplicity and to handle accounts with no pixels
  return accounts.find(account => account.id === aid);
};

const findAccountByShop = (accounts: Account[], shop: string): Account | undefined => {
  return accounts.find(account => account.accountName === shop);
};

const findAccountById = (accounts: Account[], id: string): Account | undefined => {
  return accounts.find(account => account.id === id);
};

const findPixelById = (pixels: Pixel[] | undefined, pixelId: string): Pixel | undefined => {
  return pixels?.find(pixel => pixel.id === pixelId);
};

const sortAccountsByDate = (accounts: Account[]): Account[] => {
  // Use slice() to avoid mutating the original array (preserves React Query cache immutability)
  return [...accounts].sort((a, b) => {
    const aIsActive = a.subscription?.status === 'active';
    const bIsActive = b.subscription?.status === 'active';

    if (aIsActive !== bIsActive) {
      return aIsActive ? -1 : 1;
    }

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

const EXCLUDED_PAGES = ['/onboarding', '/account/shopify-plan', '/account/shopify-plan/discount'];

export function useFetchUserAccounts(shop?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = paramsToObject(searchParams.entries());
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const accountActions = useAccountActions();
  const { accountId: storeAccountId, pixelId, isValidated } = useAccountState();
  const isSwitchInProgress = useIsAccountTransitioning();
  
  const showDialog = useGlobalDialogStore(s => s.showDialog);
  const currentUserEmail = useStore(s => s.user?.email);
  const token = useAuthToken();
  const aidParam = searchParams.get('aid');
  const currentPath = location.pathname;
  
  // Timing refs for performance logging
  const fetchStartTime = useRef<number>(0);
  const hasLoggedMount = useRef(false);
  const lastPrefetchedAccountId = useRef<string | null>(null);

  const showAccessErrorDialog = useCallback((reason: string, firstAccount: Account) => {
    const firstAccountPixelId = firstAccount.pixels?.[0]?.accountId;

    showDialog({
      title: 'Sorry we werent able to find that',
      description: (
        <div>
          <p>
            Something went wrong. You either don&apos;t have permission or that account does not exist
            anymore. Please contact the admin to get permission.
          </p>
          <p className="mt-2 font-semibold">Reverting to the first account.</p>
        </div>
      ),
      actionText: 'OK',
      onAction: () => {
        if (firstAccountPixelId) {
          setSearchParams({ ...params, aid: firstAccountPixelId });
        }
      },
    });

    Logger.error(reason);
  }, [params, setSearchParams, showDialog]);

  const selectAccount = useCallback((accounts: Account[]) => {
    const isExcludedPage = EXCLUDED_PAGES.includes(currentPath);

    if (shop) {
      return {
        account: findAccountByShop(accounts, shop),
        showDialog: false,
        reason: '',
      };
    }

    if (aidParam) {
      const account = findAccountByAid(accounts, aidParam);
      return {
        account: account ?? accounts[0],
        showDialog: !account && !isExcludedPage,
        reason: account
          ? ''
          : `Account with aid "${aidParam}" not found in accounts list, falling back to first account`,
      };
    }

    // If context already has a valid account, verify it exists in the API response
    if (storeAccountId) {
      const account = findAccountById(accounts, storeAccountId);
      if (account) {
        return {
          account,
          showDialog: false,
          reason: '',
        };
      }
      Logger.warn('Cached account not found in API response, falling back', {
        storeAccountId,
      });
    }

    // Try last persisted account ID from localStorage
    const lastAccountId = AccountStorage.getLastAccountId();
    if (lastAccountId) {
      const account = findAccountById(accounts, lastAccountId);
      if (account) {
        return {
          account,
          showDialog: false,
          reason: 'Using last persisted account',
        };
      }
    }

    return {
      account: accounts[0],
      showDialog: false,
      reason: 'No aid parameter or cached account, using first account',
    };
  }, [aidParam, currentPath, shop, storeAccountId]);

  const processAccountsResponse = useCallback((res: AxiosResponse<AccountListResponse>) => {
    const durationMs = Math.round(performance.now() - fetchStartTime.current);
    apiInfoLogger('Fetch user accounts request successfully', 'useFetchUserAccounts', res);

    Logger.debug('useFetchUserAccounts success', {
      durationMs,
      accountCount: res?.data?.accounts?.length ?? 0,
      shop,
    });

    const accounts = sortAccountsByDate(res?.data?.accounts ?? []);
    const roles = res?.data?.roles ?? [];

    accountActions.setAccountsList(accounts);
    accountActions.setRoles(roles);

    if (accounts.length === 0) {
      Logger.error('No accounts found');
      return;
    }

    // Prefetch helper extracted to shared module for reuse by switchAccount.
    // Gated on the account actually changing: this runs on every write to the
    // accounts cache, and onboarding acknowledgements write there on each click.
    const runPrefetch = (account: Account, pixelIdToUse: string) => {
      if (lastPrefetchedAccountId.current === account.id) {
        return;
      }
      lastPrefetchedAccountId.current = account.id;
      void prefetchAccountData({
        queryClient,
        account,
        pixelId: pixelIdToUse,
        roles,
        currentUserEmail,
      });
    };

    // Optimization: Skip full re-selection logic when cached account matches API response
    // Still update currentAccount with fresh data to pick up subscription/plan changes
    //
    // Conditions for early return:
    // 1. No URL aid parameter (not forcing a specific account)
    // 2. No shop parameter (not in onboarding flow)
    // 3. We have a cached account ID
    // 4. The cached account exists in the API response
    if (!aidParam && !shop && storeAccountId) {
      const existsInResponse = findAccountById(accounts, storeAccountId);
      if (existsInResponse) {
        // Account still exists - update with fresh data from API to pick up any changes
        // (e.g., subscription plan changes from Stripe billing settings)
        accountActions.setCurrentAccount(existsInResponse);

        try {
          if (!isValidated) {
            accountActions.markValidated();
          }
        } catch (error) {
          Logger.error('Failed to mark account validated', error as Error, {
            accountId: storeAccountId,
          });
        }

        Logger.debug('Account validated from cache, updated with fresh API data', {
          accountId: storeAccountId,
          wasAlreadyValidated: isValidated,
        });

        // Still run prefetch for validated accounts
        const existingPixelId = existsInResponse.pixels?.[0]?.id;
        if (existingPixelId) {
          runPrefetch(existsInResponse, existingPixelId);
        }
        return;
      }
      // Cache miss - cached account not in API response, continue to full selection
      Logger.debug('Cached account not found in API response, running full selection', {
        storeAccountId,
        accountCount: accounts.length,
      });
    }

    const {
      account: selectedAccount,
      showDialog: shouldShowDialog,
      reason,
    } = selectAccount(accounts);

    if (!selectedAccount) {
      Logger.error('Failed to select any account');
      return;
    }

    const isUrlDrivenSwitch = !!aidParam && !!storeAccountId && storeAccountId !== selectedAccount.id;

    // The URL still holds the old aid until a switch in progress finishes navigating.
    if (isUrlDrivenSwitch && isSwitchInProgress) {
      return;
    }

    if (shouldShowDialog) {
      showAccessErrorDialog(reason, selectedAccount);
    }

    const selectedPixel = pixelId
      ? findPixelById(selectedAccount.pixels, pixelId)
      : selectedAccount.pixels?.[0];

    const finalPixelId = selectedPixel?.id ?? selectedAccount.pixels?.[0]?.id;

    if (!finalPixelId) {
      Logger.error('No pixel found for selected account');
    }

    if (isUrlDrivenSwitch) {
      // Same cleanup as the switcher, which also prefetches, but stay on this URL.
      toast.info(`Switched to ${selectedAccount.accountName}`);
      void accountActions.switchWorkspace(selectedAccount.id, { accounts, preserveLocation: true });
    } else {
      accountActions.setCurrentAccount(selectedAccount);
      if (finalPixelId) {
        runPrefetch(selectedAccount, finalPixelId);
      }
    }
    accountActions.markValidated();

    if (shop && finalPixelId) {
      setSearchParams({
        ...params,
        pixelId: finalPixelId,
      });
    }

    if (location.pathname.includes('login')) {
      void navigate('/');
    }
  }, [
    accountActions,
    aidParam,
    currentUserEmail,
    isSwitchInProgress,
    isValidated,
    navigate,
    params,
    pixelId,
    queryClient,
    selectAccount,
    setSearchParams,
    shop,
    showAccessErrorDialog,
    storeAccountId,
  ]);

  const processedResponseRef = useRef<AxiosResponse<AccountListResponse> | undefined>(
    undefined
  );
  const processedSelectionContextRef = useRef<string | undefined>(undefined);
  const selectionContextKey = `${shop ?? ''}:${aidParam ?? ''}`;
  const { data, status, isError, isLoading, error, refetch, fetchStatus } = useAuthenticatedQuery(
    accountKeys.list(),
    () => {
      fetchStartTime.current = performance.now();
      Logger.debug('useFetchUserAccounts fetching', { shop, hasToken: !!token });
      return AccountsApiService.getUserAccounts();
    },
    {
      useErrorBoundary: false,
      enabled: token !== null && token !== undefined && token !== '',
      staleTime: 1000 * 60 * 60, // 1 hour
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours — keep cache across navigation/unmounts
      refetchOnWindowFocus: false,
      refetchOnMount: false, // rely on explicit invalidation via invalidateUserData
      refetchOnReconnect: false,
      onSuccess: (res: AxiosResponse<AccountListResponse>) => {
        processedResponseRef.current = res;
        processedSelectionContextRef.current = selectionContextKey;
        processAccountsResponse(res);
      },
      onError: (error: AxiosError) => {
        const durationMs = Math.round(performance.now() - fetchStartTime.current);
        apiErrorLogger('Fetch user accounts request failed', 'useFetchUserAccounts', error);
        
        Logger.warn('useFetchUserAccounts error', {
          durationMs,
          errorMessage: error.message,
          errorStatus: error.response?.status,
          shop,
        });
        
        const isNetworkError = !error.response;
        const message = isNetworkError
          ? 'Unable to load accounts. Please check your connection.'
          : 'Failed to load accounts. Please try again.';
        
        toast.error(message);
      },
    }
  );

  // AccountProvider may have populated the shared query before this initializer
  // mounted. Process that cached response so selection, validation, and prefetch
  // still run without issuing a second accounts request.
  useEffect(() => {
    if (
      !data ||
      (processedResponseRef.current === data &&
        processedSelectionContextRef.current === selectionContextKey)
    ) {
      return;
    }

    processedResponseRef.current = data;
    processedSelectionContextRef.current = selectionContextKey;
    processAccountsResponse(data);
  }, [data, processAccountsResponse, selectionContextKey]);

  // Log mount state for tracing
  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      Logger.debug('useFetchUserAccounts mounted', {
        shop,
        hasToken: !!token,
        storeAccountId,
        isValidated,
        aidParam,
      });
    }
  }, [shop, token, storeAccountId, isValidated, aidParam]);

  // Log query state changes for debugging
  useEffect(() => {
    Logger.debug('useFetchUserAccounts state', {
      isLoading,
      fetchStatus,
      status,
      isError,
      hasData: !!data,
    });
  }, [isLoading, fetchStatus, status, isError, data]);

  return {
    data,
    status,
    isError,
    isLoading,
    error: error,
    refetch,
    fetchStatus,
  };
}
